import { useRemoteResource } from "./useRemoteResource";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #2131 (docs/score/ring-truth.md) — the ring's own stored ring-state row,
// the ONE source of "what class is in progress in this ring" (rules 5+8) and
// therefore of the CURRENT/ON_DECK roles orderOfGo.js used to derive from ride
// statuses instead (the 9am-ghost bug: an abandoned ride's own open-scoresheet
// IN_PROGRESS status outlived the class the scribe had actually moved on to,
// because nothing revoked it — exactly what rule 3 forbids and #1934's store
// fixes by construction). Read-only here — announcer-fe never writes ring
// state, same as it never writes a score.
//
// #2190 — no local cache. getRingState's 200 response already carries `null`
// data for a genuinely-empty ring ("Ring state (null data if never set)",
// apps/score-api/src/handlers/ring-state.js) — that is real data, not a
// failure, so it must flow straight to useRemoteResource as a successful
// fetch and update the role assignment. The ONLY way this fetcher's promise
// rejects is a real transport/HTTP failure (network down, 5xx). Letting that
// propagate (no try/catch here) is what makes useRemoteResource's own "leave
// last-rendered data alone on failure" contract apply to ring state: a
// connectivity loss must only stop refresh, not silently overwrite the
// in-ring CURRENT/ON_DECK assignment with null. Swallowing the error here
// and returning null instead used to look equivalent to "no ring state," but
// it made every transient failure indistinguishable from a real 200-null
// response, clobbering already-loaded ring state on every dropped request
// (#2190 offline-degraded-mode.cy.js).
//
// #2197 — `undefined` (not `null`) is deliberate for the "ring not resolved
// yet" branch. `selectedRing` starts null and resolves asynchronously
// (#1857/#2112), so this hook's FIRST-ever effect run often fires with no
// ring at all; useRemoteResource settles that as a real, successful answer
// and never resets `loading` back to true on the later effect run that fires
// once a real ring lands (its own documented contract). Without a distinct
// sentinel, that later real fetch's in-flight window is indistinguishable
// from "server confirmed this ring has no live class" (both read as data:
// null, loading: false) — the exact race that let OrderOfGo.jsx's
// oog-no-class-in-progress banner render before the real answer had
// arrived. `undefined` here, contrasted with a genuine 200-null response
// from getRingState, gives OrderOfGo.jsx an honest "not answered yet" it can
// gate on (see its ringStateKnown).
// #2217 — `[showId, ring]` as `resourceKey` (docs in useRemoteResource.js):
// OrderOfGo is not remounted on a ring switch, so without this the hook
// treated a ring switch as just another revalidate of the OLD ring's row
// and kept showing it (CURRENT/ON_DECK or the "no class in progress"
// banner) through the whole in-flight window. `nonce` (the poll/refetch
// beat) is deliberately NOT part of the key — only `showId`/`ring` identify
// which resource this is, so a plain revalidate still keeps #2190's "never
// re-blank" contract.
//
// #2227: `undefined` as the 4th arg (`initialValue`, docs in
// useRemoteResource.js) is what a `resourceKey` reset restores AND what a
// failed FIRST fetch on the new key settles to, instead of #2217's
// hard-coded `null`. `undefined` is this hook's own "ring not resolved
// yet" sentinel (the comment above), so a reset and a cold-start failure
// on the new ring now read exactly like "not answered yet": OrderOfGo's
// `ringStateKnown` stays false and its honest loading indicator renders,
// never the `oog-no-class-in-progress` banner off a fetch that never
// landed.
export function useRingStateForRing(showId, ring, nonce = 0) {
  const api = useScoreApi();
  return useRemoteResource(
    async () => {
      if (!showId || !ring) return undefined;
      return unwrap(await api.ringState.getRingState(showId, ring));
    },
    [api, showId, ring, nonce],
    [showId, ring],
    undefined,
  );
}

export default useRingStateForRing;
